import React from 'react';
import { useNavigate } from 'react-router-dom';
import './ChoiceWheel.css';

/**
 * Radial navigation layout used by landing pages (Admin, Time Off).
 *
 * items: [{ key, title, icon, path, description?, onClick?, badge?, tag? }]
 *   badge is a count sat on the tile — how much is waiting behind it. Rendered
 *   whenever it is given, zero included: on a queue, "0" is the answer.
 *   tag is a word about the tile itself rather than a count - "Preview" on
 *   something still new - as a small pill. Not used together with badge.
 * title: page name, rendered in the middle of the ring (these pages do not
 *   carry their own heading).
 * reservedHeight: vertical space anything above the wheel takes, so the wheel
 *   can center itself in what is left of the viewport.
 * back: { label, path } for a wheel nested under another one (Configuration
 *   under Admin). Rendered as a button in the middle of the ring, under the
 *   title.
 */
export default function ChoiceWheel({ items = [], title, reservedHeight = 60, emptyText = 'Nothing available.', back }) {
  const navigate = useNavigate();

  const backButton = back ? (
    <button type="button" className="bdc-btn bdc-btn--sm choice-wheel-back" onClick={() => navigate(back.path)}>
      <i className="bi bi-arrow-left" aria-hidden="true" />
      Back to {back.label}
    </button>
  ) : null;

  if (items.length === 0) {
    return (
      <div className="d-flex flex-column align-items-start gap-2">
        {title ? <h3 className="m-0">{title}</h3> : null}
        <div className="text-muted">{emptyText}</div>
        {backButton}
      </div>
    );
  }

  return (
    <div className="choice-wheel-stage" style={{ '--choice-wheel-reserved': `${reservedHeight}px` }}>
      <div className="choice-wheel">
        <div className="choice-wheel-ring" />

        {title || backButton ? (
          <div className="choice-wheel-center">
            {title ? <h3 className="choice-wheel-title">{title}</h3> : null}
            {backButton}
          </div>
        ) : null}

        {items.map((item, index) => {
          // Start at the top of the ring and spread the tiles evenly around it.
          const angle = (-Math.PI / 2) + ((2 * Math.PI * index) / items.length);
          return (
            <button
              key={item.key}
              type="button"
              className="choice-wheel-item"
              style={{
                '--cos': Math.cos(angle).toFixed(4),
                '--sin': Math.sin(angle).toFixed(4),
                '--i': index,
              }}
              title={item.description}
              onClick={() => (item.onClick ? item.onClick() : navigate(item.path))}
            >
              <span className="choice-wheel-dot">
                <i className={`bi ${item.icon}`} />
                {item.badge != null ? (
                  <span className="choice-wheel-badge">{item.badge}</span>
                ) : null}
                {item.tag ? (
                  <span className="choice-wheel-tag">{item.tag}</span>
                ) : null}
              </span>
              <span className="choice-wheel-label">{item.title}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
